import { useCallback, useEffect, useRef, useState } from 'react'
import {
  billingApi,
  type AvailablePlansResponse,
  type BillingPlan,
  type PricingExperimentInfo,
} from '@/services/billingApi'

const PRICING_VARIANT_STORAGE_KEY = 'ricald_pricing_variant'
const PRICING_VISITOR_STORAGE_KEY = 'ricald_pricing_visitor'

function getQueryParamVariant(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const params = new URLSearchParams(window.location.search)
  return params.get('pricing_variant') || params.get('pv')
}

function getStoredVariant(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  return window.localStorage.getItem(PRICING_VARIANT_STORAGE_KEY)
}

function setStoredVariant(variant: string): void {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(PRICING_VARIANT_STORAGE_KEY, variant)
}

function getOrCreateVisitorId(): string {
  if (typeof window === 'undefined') {
    return 'server'
  }

  const existing = window.localStorage.getItem(PRICING_VISITOR_STORAGE_KEY)
  if (existing) {
    return existing
  }

  const created = `v-${Math.random().toString(36).slice(2, 12)}`
  window.localStorage.setItem(PRICING_VISITOR_STORAGE_KEY, created)
  return created
}

function pickDeterministicVariant(availableVariants: string[]): string | null {
  if (!availableVariants.length) {
    return null
  }

  const visitorId = getOrCreateVisitorId()
  let hash = 0
  for (let i = 0; i < visitorId.length; i += 1) {
    hash = (hash * 31 + visitorId.charCodeAt(i)) >>> 0
  }
  return availableVariants[hash % availableVariants.length]
}

interface PricingPlansState {
  plans: BillingPlan[]
  experiment: PricingExperimentInfo | null
  marketingCopy: Record<string, string>
  loading: boolean
  error: string | null
}

function toState(
  payload: AvailablePlansResponse,
  loading: boolean,
  error: string | null
): PricingPlansState {
  return {
    plans: payload.plans ?? [],
    experiment: payload.experiment ?? null,
    marketingCopy: payload.marketing_copy ?? {},
    loading,
    error,
  }
}

export function usePricingPlans() {
  const [state, setState] = useState<PricingPlansState>({
    plans: [],
    experiment: null,
    marketingCopy: {},
    loading: true,
    error: null,
  })

  const mountedRef = useRef(true)
  // RE-ARM on effect start. Under StrictMode's mount→cleanup→remount, the
  // cleanup sets this false and nothing ever set it true again — so every
  // response was discarded and the landing page silently rendered FALLBACKS in
  // dev while the API served the configured copy. (#770 carried this fix; the
  // reconstruction initially ported the landing files without it, and the
  // generation guard below made the discard look intentional.)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Round-8 review: the mounted flag alone lets an OLDER request overwrite a
  // NEWER response — two overlapping load() calls (StrictMode double-invoke, a
  // variant change racing the initial load) both pass the mounted check, and
  // whichever network round-trip finishes LAST wins regardless of which was
  // issued last. A generation counter makes only the newest call's responses
  // land; stale generations return silently at every await point.
  const generationRef = useRef(0)

  const load = useCallback(async (variant?: string) => {
    const generation = ++generationRef.current
    const isCurrent = () => mountedRef.current && generation === generationRef.current
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const first = await billingApi.getAvailablePlansWithExperiment(variant)
      if (!isCurrent()) return

      const hasExplicitVariant = Boolean(variant)
      const experiment = first.experiment
      const available = experiment?.available_variants ?? []

      if (!hasExplicitVariant && experiment?.enabled && available.length > 0) {
        const chosen = getStoredVariant() || pickDeterministicVariant(available)

        if (chosen) {
          setStoredVariant(chosen)
          if (chosen !== experiment.variant) {
            const second = await billingApi.getAvailablePlansWithExperiment(chosen)
            if (!isCurrent()) return
            setState(toState(second, false, null))
            return
          }
        }
      }

      setState(toState(first, false, null))
    } catch (error) {
      if (!isCurrent()) return
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load pricing plans',
      }))
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const queryVariant = getQueryParamVariant()
    const storedVariant = getStoredVariant()
    const initialVariant = queryVariant || storedVariant || undefined

    if (queryVariant) {
      setStoredVariant(queryVariant)
    }

    if (!cancelled) {
      load(initialVariant)
    }
    return () => { cancelled = true }
  }, [load])

  const setVariant = useCallback(
    async (variant: string) => {
      setStoredVariant(variant)
      await load(variant)
    },
    [load]
  )

  return {
    plans: state.plans,
    experiment: state.experiment,
    marketingCopy: state.marketingCopy,
    loading: state.loading,
    error: state.error,
    reload: load,
    setVariant,
  }
}

export default usePricingPlans
